import React, { useState } from 'react';
import { ShareIcon, LinkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ShareButton = ({ project }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    const projectUrl = `${baseUrl}/projects/${project.id}`;
    setShareUrl(projectUrl);
    return projectUrl;
  };

  const handleShare = () => {
    generateShareUrl();
    setShowShareModal(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié dans le presse-papiers !');
    } catch (error) {
      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Lien copié dans le presse-papiers !');
    }
  };

  const shareOnSocialMedia = (platform) => {
    const url = shareUrl || generateShareUrl();
    const text = `Découvrez ce projet : ${project.title}`;
    
    let shareUrl_platform = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl_platform = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl_platform = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl_platform = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl_platform = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl_platform, '_blank', 'width=600,height=400');
  };

  const shareByEmail = () => {
    const url = shareUrl || generateShareUrl();
    const subject = `Découvrez ce projet : ${project.title}`;
    const body = `Bonjour,\n\nJe voulais partager avec vous ce projet intéressant :\n\n${project.title}\n${project.description.substring(0, 200)}...\n\nLien : ${url}\n\nCordialement`;
    
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  return (
    <>
      <button
        onClick={handleShare}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
        title="Partager ce projet"
      >
        <ShareIcon className="h-5 w-5" />
        <span className="text-sm font-medium">Partager</span>
      </button>

      {/* Modal de partage */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text">Partager ce projet</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* Lien direct */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lien direct
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                      title="Copier le lien"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Réseaux sociaux */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Partager sur les réseaux sociaux
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => shareOnSocialMedia('facebook')}
                      className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <span className="text-sm font-medium">Facebook</span>
                    </button>
                    <button
                      onClick={() => shareOnSocialMedia('twitter')}
                      className="flex items-center justify-center px-3 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                    >
                      <span className="text-sm font-medium">Twitter</span>
                    </button>
                    <button
                      onClick={() => shareOnSocialMedia('linkedin')}
                      className="flex items-center justify-center px-3 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                    >
                      <span className="text-sm font-medium">LinkedIn</span>
                    </button>
                    <button
                      onClick={() => shareOnSocialMedia('whatsapp')}
                      className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <span className="text-sm font-medium">WhatsApp</span>
                    </button>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <button
                    onClick={shareByEmail}
                    className="w-full flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">Partager par email</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;

